import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Avatar,
  Switch,
  FormControlLabel,
  useTheme,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';

const AccessRights = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users');
      // Только одобренные пользователи
      setUsers(response.data.filter(u => u.status === 'approved'));
    } catch (error) {
      console.error('Ошибка загрузки пользователей:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'manager' : 'admin';
    setUpdating(userId);
    
    try {
      await axios.patch(`/api/users/${userId}/role`, { role: newRole });
      setUsers(users.map(u => 
        u._id === userId ? { ...u, role: newRole } : u
      ));
      setSnackbar({
        open: true,
        message: `Роль успешно изменена на ${newRole === 'admin' ? 'Администратор' : 'Менеджер'}`,
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Ошибка изменения роли',
        severity: 'error'
      });
    } finally {
      setUpdating(null);
    }
  };

  const managers = users.filter(u => u.role === 'manager');
  const admins = users.filter(u => u.role === 'admin');

  const UserCard = ({ user, index = 0 }) => {
    const isAdmin = user.role === 'admin';
    
    return (
      <Card
        sx={{
          background: isDarkMode ? 'rgba(22, 27, 34, 0.6)' : '#ffffff',
          backdropFilter: isDarkMode ? 'blur(20px)' : 'none',
          border: '1px solid',
          borderColor: isAdmin 
            ? 'rgba(180, 142, 173, 0.3)' 
            : isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s ease',
          animation: `fadeInUp 0.4s ease ${index * 0.05}s both`,
          '@keyframes fadeInUp': {
            '0%': { opacity: 0, transform: 'translateY(20px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' },
          },
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: isDarkMode 
              ? '0 12px 40px rgba(0, 0, 0, 0.4)'
              : '0 12px 40px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                mr: 2,
                fontWeight: 700,
                background: isAdmin 
                  ? 'linear-gradient(135deg, #b48ead 0%, #bf616a 100%)'
                  : 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)',
                boxShadow: isDarkMode 
                  ? '0 4px 12px rgba(0, 0, 0, 0.3)'
                  : '0 4px 12px rgba(0, 0, 0, 0.1)',
              }}
            >
              {user.firstName?.[0]}{user.lastName?.[0]}
            </Avatar>
            <Box flex={1}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {user.firstName} {user.lastName}
              </Typography>
              <Chip
                icon={isAdmin ? <AdminIcon sx={{ fontSize: 16 }} /> : <PersonIcon sx={{ fontSize: 16 }} />}
                label={isAdmin ? 'Администратор' : 'Менеджер'}
                size="small"
                sx={{
                  mt: 0.5,
                  background: isAdmin
                    ? 'rgba(180, 142, 173, 0.2)'
                    : 'rgba(136, 192, 208, 0.2)',
                  color: isAdmin ? '#b48ead' : '#88c0d0',
                  border: '1px solid',
                  borderColor: isAdmin 
                    ? 'rgba(180, 142, 173, 0.3)' 
                    : 'rgba(136, 192, 208, 0.3)',
                  fontWeight: 600,
                  '& .MuiChip-icon': {
                    color: isAdmin ? '#b48ead' : '#88c0d0',
                  }
                }}
              />
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
              📧 {user.email}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              📱 {user.phone}
            </Typography>
          </Box>

          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              p: 2,
              borderRadius: 2,
              background: isDarkMode 
                ? 'rgba(255, 255, 255, 0.03)' 
                : 'rgba(0, 0, 0, 0.02)',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Права администратора
            </Typography>
            {updating === user._id ? (
              <CircularProgress size={24} />
            ) : (
              <Switch
                checked={isAdmin}
                onChange={() => handleRoleChange(user._id, user.role)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#b48ead',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#b48ead',
                  },
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Layout>
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="60vh"
          flexDirection="column"
          gap={2}
        >
          <CircularProgress size={60} thickness={2} />
          <Typography variant="body2" color="text.secondary">
            Загрузка пользователей...
          </Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ mb: 4 }}>
        <Box display="flex" alignItems="center" gap={2} mb={1}>
          <SecurityIcon sx={{ fontSize: 40, color: '#b48ead' }} />
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 800,
              background: isDarkMode 
                ? 'linear-gradient(135deg, #e6edf3 0%, #b48ead 100%)'
                : 'linear-gradient(135deg, #2e3440 0%, #b48ead 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Права доступа
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Управляйте правами пользователей. Включите переключатель, чтобы дать права администратора.
        </Typography>
      </Box>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              background: 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)',
              border: 'none',
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center">
                <PersonIcon sx={{ fontSize: 50, mr: 2, color: '#0d1117' }} />
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: '#0d1117' }}>
                    {managers.length}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(0,0,0,0.7)' }}>
                    Менеджеров
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              background: 'linear-gradient(135deg, #b48ead 0%, #bf616a 100%)',
              border: 'none',
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center">
                <AdminIcon sx={{ fontSize: 50, mr: 2, color: '#0d1117' }} />
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: '#0d1117' }}>
                    {admins.length}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(0,0,0,0.7)' }}>
                    Администраторов
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Список пользователей */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: 'text.primary' }}>
        Все пользователи
      </Typography>
      
      <Grid container spacing={3}>
        {users.length > 0 ? (
          users.map((user, index) => (
            <Grid item xs={12} md={6} lg={4} key={user._id}>
              <UserCard user={user} index={index} />
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Box textAlign="center" py={8}>
              <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                Нет пользователей
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default AccessRights;
